import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, User, LoginCredentials, RegisterCredentials } from '../types';
import { loginUser, registerUser, getCurrentUser } from '../utils/api';

// Auth Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

// Auth Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

// Initial State
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

// Context
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials, recaptchaToken?: string | null) => Promise<void>;
  register: (credentials: RegisterCredentials, recaptchaToken?: string | null) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Verify token and get user info
      getCurrentUser(token)
        .then((user) => {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token },
          });
        })
        .catch(() => {
          // Token is invalid, remove it
          localStorage.removeItem('auth_token');
          dispatch({ type: 'AUTH_FAILURE' });
        });
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const login = async (credentials: LoginCredentials, recaptchaToken?: string | null) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const authResponse = await loginUser(credentials, recaptchaToken);
      const user = await getCurrentUser(authResponse.access_token);
      
      // Store token in localStorage
      localStorage.setItem('auth_token', authResponse.access_token);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: authResponse.access_token },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials, recaptchaToken?: string | null) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const user = await registerUser(credentials, recaptchaToken);
      
      // Auto-login after registration (reCAPTCHA not needed for auto-login)
      const authResponse = await loginUser({
        username: credentials.username,
        password: credentials.password,
      });
      
      // Store token in localStorage
      localStorage.setItem('auth_token', authResponse.access_token);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: authResponse.access_token },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    dispatch({ type: 'LOGOUT' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

